// react documenation used: https://react.dev/learn
// axios documentation used: https://axios-http.com/docs/intro
import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./adminPanel.module.css";

function AdminPanel() {
  // these variables will store the states of the react page storing either current values from API calls or user interactions with the admin form
  const [users, setUsers] = useState([]); // the set functions allow the variables to be updated after calling the function
  const [repositories, setRepositories] = useState([]); // the value inside the brackets set the default value for the variables
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserRole, setSelectedUserRole] = useState("");
  const [selectedAccessLevel, setSelectedAccessLevel] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;
  const [selectedRepoId, setSelectedRepoId] = useState("");

  // the functions inside the useEffect function run as soon as the page loads.
  useEffect(() => {
    const fetchUsers = async () => {
      // the token is taken from the client side storage which will be fulfilled when the user is authenticatedd
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`${apiUrl}/api/users/getusers`, {
          headers: { Authorization: `Bearer ${token}` }, // the token is passed through the axios get request using the headers function
        });
        // axios reposonse is pathed to the correct value then the users are set to store the information pulled from the backend inside the users variable
        setUsers(response.data.recordsets[0]);
        if (response.data.length > 0) {
          // stores the value of the current user logged in and whether they are a admin or user, the data is obtained from the reponse.
          setSelectedUserId(response.data[0].id);
          setSelectedUserRole(response.data[0].isAdmin ? "Admin" : "User");
        }
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };

    const fetchRepositories = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/requests/repositories`);
        setRepositories(response.data);
      } catch (error) {
        console.error("Failed to fetch repositories", error);
      }
    };
    fetchUsers();
    fetchRepositories();
  });

  const handleRoleChange = async () => {
    const token = localStorage.getItem("token");
    const isAdmin = selectedUserRole === "Admin";
    try {
      await axios.put(
        `${apiUrl}/api/users/role/${selectedUserId}`,
        { selectedUserId, isAdmin },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert("User role updated successfully");
    } catch (error) {
      console.error("Failed to update user role", error);
    }
  };

  const handleDeleteUser = async () => {
    const token = localStorage.getItem("token");
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this user?"
    );

    if (isConfirmed) {
      try {
        await axios.delete(`${apiUrl}/api/users/delete/${selectedUserId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("User deleted successfully.");
        setUsers(users.filter((user) => user.id !== selectedUserId));
      } catch (error) {
        // only comes to this stage if there is an unexpected error that doesnt fall inline with what should recieved from the database.
        if (error.response && error.response.data) {
          alert(error.response.data);
        } else {
          console.error("Failed to delete user", error);
          alert("Failed to delete user.");
        }
      }
    }
  };

  const resetPassword = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${apiUrl}/api/users/resetpassword/${selectedUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Password reset successfully");
    } catch (error) {
      console.error("Failed to reset password", error);
    }
  };

  const deleteRepository = async (selectedRepoId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this repository?"
    );
    if (!isConfirmed) return;

    try {
      await axios.delete(
        `${apiUrl}/api/requests/deleteRepository/${selectedRepoId}`
      );
      setRepositories(
        // removes the deleted repository from the repo list so that there doesnt need to be another api call to fetch the new list of repositories
        repositories.filter((repo) => repo.repository_id !== selectedRepoId)
      );
      alert("Repository deleted successfully");
    } catch (error) {
      console.error("Failed to delete repository", error);
    }
  };

  const handleAccessLevelChange = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${apiUrl}/api/users/${selectedUserId}/access-level`,
        { selectedUserId, selectedAccessLevel },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("User access level updated successfully");
    } catch (error) {
      console.error("Failed to update user access level", error);
    }
  };

  return (
    <div className={styles.adminPanel}>
      <h2>Admin Panel</h2>
      <label>Select User: </label>
      <select
        onChange={(e) => {
          const userId = e.target.value;
          const selectedUser = users.find(
            (user) => user.user_id.toString() === userId
          );
          if (selectedUser) {
            setSelectedUserId(userId);
            setSelectedUserRole(selectedUser.isAdmin ? "Admin" : "User");
          }
        }}
        value={selectedUserId}
      >
        {users.map((user) => (
          // displayes each user seperately from the users list
          <option key={user.id} value={user.user_id.toString()}>
            {user.first_name} {user.last_name}
          </option>
        ))}
      </select>

      <br />
      <label>User Role: </label>
      <select
        onChange={(e) => setSelectedUserRole(e.target.value)}
        value={selectedUserRole}
      >
        <option value="User">User</option>
        <option value="Admin">Admin</option>
      </select>
      <br />

      <br />
      <label>Access Level:</label>
      <select
        onChange={(e) => {
          setSelectedAccessLevel(e.target.value);
        }}
        value={selectedAccessLevel}
      >
        <option value="confidential">Confidential</option>
        <option value="non-confidential">Non-Confidential</option>
      </select>
      <label>Repository:</label>
      <select
        value={selectedRepoId}
        onChange={(e) => setSelectedRepoId(e.target.value)}
      >
        {repositories.map((repo) => (
          <option key={repo.gitRepoID} value={repo.gitRepoID}>
            {repo.repository_name}
          </option>
        ))}
      </select>
      <br />

      <button onClick={handleRoleChange}>Update Role</button>
      <button onClick={handleAccessLevelChange}>Update Access level</button>
      <button onClick={handleDeleteUser} className={styles.deleteButton}>
        Delete User
      </button>

      <button
        onClick={() => resetPassword(selectedUserId)}
        className={styles.deleteButton}
      >
        Reset Password
      </button>

      <button
        onClick={() => deleteRepository(selectedRepoId)}
        className={styles.deleteButton}
      >
        Delete Repository
      </button>
    </div>
  );
}

export default AdminPanel;
